import { IoAlert } from 'react-icons/io5';

import { Alert, Button, Modal, Space, Stack, Text } from '@mantine/core';

import { SubscriptionFromSubscriptionType, SubscriptionType } from 'models';
import { SubscriptionConfigHelpers } from 'helpers/SubscriptionConfigHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { Form } from 'components/forms/Form';
import { MultilineTextField } from 'components/forms/fields/MultilineTextField';
import { ModalTitle } from 'components/modals/ModalTitle';
import { SubscriptionUpdateCommonProps } from 'components/subscriptions/types';
import { TeamService } from 'services/TeamService';

import { SubscriptionFeatureList } from '../../subscription-feature-list/SubscriptionFeatureList';
import { ContactSalesButton } from '../ContactSales';
import { useSubscriptionUnsubscribe } from '../useSubscriptionUnsubscribe';

export const Unsubscribe = <T extends SubscriptionType>({
    type,
    toSubscription,
    ...subscriptionProps
}: {
    type: T;
    toSubscription: SubscriptionFromSubscriptionType<T>;
} & SubscriptionUpdateCommonProps) => {
    const { team } = subscriptionProps;

    const { submitSubscriptionUnsubscribe, loading } = useSubscriptionUnsubscribe({
        type,
        ...subscriptionProps,
    });

    const currentSubscription = SubscriptionHelpers.getSubscriptionData(type, team.subscriptions).subscription;

    const title = SubscriptionConfigHelpers.getSubscriptionConfigByType(type)[currentSubscription].title;

    return (
        <>
            <Modal.Header>
                <Modal.Title>
                    <ModalTitle title="Unsubscribe" />
                </Modal.Title>
                <Modal.CloseButton />
            </Modal.Header>
            <Modal.Body>
                <Stack gap="lg">
                    <Text>
                        Please confirm that you want to unsubscribe from the <strong>{title}</strong> plan. Your
                        subscription will be canceled at the end of your current billing period and you will lose access
                        to all premium features:
                    </Text>

                    {currentSubscription ? (
                        <SubscriptionFeatureList type={type} subscription={currentSubscription} checkmark={false} />
                    ) : null}

                    {SubscriptionHelpers.isSubscriptionType(SubscriptionType.DESIGNER, toSubscription) ? (
                        <Alert variant="outline" color="red" icon={<IoAlert size={20} />}>
                            Any unused credits will remain available until your subscription ends at the conclusion of
                            your current billing period.
                        </Alert>
                    ) : null}

                    <Form<{ feedback: string }>
                        defaultValues={{ feedback: '' }}
                        onSubmit={(values) => {
                            TeamService.sendSubscriptionFeedback(values.feedback).then();

                            submitSubscriptionUnsubscribe();
                        }}
                    >
                        <Stack gap="xs">
                            <MultilineTextField
                                label="Feedback"
                                name="feedback"
                                placeholder="Please share why you decided to unsubscribe"
                            />
                            <Space />
                            <Button variant="filled" color="red" size="md" loading={loading} type="submit">
                                Unsubscribe
                            </Button>
                            <ContactSalesButton />
                        </Stack>
                    </Form>
                </Stack>
            </Modal.Body>
        </>
    );
};
